import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import "./i18n";
import "./main.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./api/query-client";
import InstallButton from "./components/PWA/installButton";
import { HelmetProvider } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConsentButton from "./components/Cookies/consentButton";
import config from "@/config/config";
import ReactPixel from "react-facebook-pixel";
import { registerSW } from "virtual:pwa-register";
import ReactGA from "react-ga4";

// Initialize GA4 - config is applied after consent is granted
ReactGA.initialize("G-2H8P3FCSEL");

// Initialize Meta Pixel - will be gated by consent in ConsentButton
try {
  if (localStorage.getItem("cookie_consent") === "true") {
    ReactPixel.init(config.pixelID);
  }
} catch (e) {
  // localStorage is not available (SSR)
}

async function enableMocking() {
  const isMocking = import.meta.env.VITE_MOCKING === "true";
  if (!isMocking || process.env.NODE_ENV !== "development") {
    return Promise.resolve();
  }
  const { worker } = await import("./mocks/browser");
  return worker.start({ onUnhandledRequest: "bypass" });
}

registerSW({ immediate: true });

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

Sentry.init({
  dsn: config.sentryDsn,
  sendDefaultPii: true,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

const root = ReactDOM.createRoot(rootElement);

function render() {
  root.render(
    <Sentry.ErrorBoundary fallback={<div>An error has occurred</div>}>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
          <ConsentButton />
          <InstallButton />
          <ToastContainer />
        </QueryClientProvider>
      </HelmetProvider>
    </Sentry.ErrorBoundary>,
  );
}

enableMocking().then(() => {
  render();
});
